import { acceptHMRUpdate, defineStore } from "pinia";
import { computed, ref } from "vue";

import type { IAddArticleToCartCmd, IArticleWithOptions } from "@/data/commands/customer/AddArticleToCartCmd";
import { Factory } from "@/data/dao/Factory";
import { DaoError } from "@/data/dao/service/BaseServiceDao";
import { CustomerDao } from "@/data/dao/service/CustomerDao";
import type { ICartItem, ICustomerCart } from "@/data/model/Customer";
import type { IService } from "@/data/model/Service";
import {  exceptionTypes } from "@/data/util/Config";

import { translations } from "@/shared/localization/translations";
import { logError } from "@/shared/logging/errors";
import { TaskQueue } from "@/shared/utils/taskQueue";

import { Popup } from "@/components/dialog/Popup";

import { useTableAllocationStore } from "./tableAllocation";

export const useCartStore = defineStore("cart", () => {
    const customerDao = Factory.getInstance<CustomerDao>(CustomerDao);
    const allocation = useTableAllocationStore();

    // define state
    const currentCart = ref<ICustomerCart | null>(null);
    const isUpdating = ref<boolean>(false);

    // define getters
    const totalAmount = computed(() => currentCart.value?.total ?? 0);
    const totalAfterDiscountsAndVouchers = computed(() => currentCart.value?.totalAfterDiscountsAndVouchers ?? 0);
    const totalCartItems = computed(() => {
        let articles = 0;
        for (const item of items.value) {
            articles += item.quantity;
        }

        return articles;
    });
    const items = computed(() => currentCart.value?.items ?? []);
    const hasItems = computed(() => items.value.length > 0);
    const hasDiscountsOrVouchers = computed(() => (currentCart.value?.totalDiscountsAndVouchersAmount ?? 0) > 0);
    const orderServiceId = computed(() => currentCart.value?.serviceId ?? null);
    const orderService = computed(() => {
        const orderServices = allocation.orderServices.filter((x) => x.id == orderServiceId.value);
        if (orderServices.length > 1) {
            throw "Unexpected number of services.";
        }
        return allocation.hasOrderServices ? orderServices[0] : null;
    });
    const areItemCommentsAllowed = computed(() => orderService.value?.useCase?.areItemCommentsAllowed ?? false);

    const cartRequestsQueue = new TaskQueue();

    // define actions
    async function addArticleToCart(
        articleId: string,
        menuCategoryId: string,
        quantity: number,
        options: IArticleWithOptions[],
        orderService: IService,
        offerId: string,
        openPrice?: number
    ) {
        await cartRequestsQueue.enqueue(async () => {
            try {
                const data: IAddArticleToCartCmd = {
                    article: {
                        articleId: articleId,
                        menuCategoryId: menuCategoryId,
                        quantity: quantity,
                        options: options,
                        openPrice: openPrice,
                    },
                    orderServiceId: orderService.id,
                    offerId: offerId,
                };

                if (hasDiscountsOrVouchers.value) {
                    await showConfirmDiscountsRemovalPopup(() => processAddArticleToCart(data));
                    console.log("after showConfirmDiscountsRemovalPopup");
                } else {
                    await processAddArticleToCart(data);
                }
            } catch (ex) {
                logError(ex);
                await handleError(ex, translations.cart.failedToAdd);
            }
        });
    }

    async function processAddArticleToCart(data: IAddArticleToCartCmd) {
        isUpdating.value = true;

        try {
            const newCart = await customerDao.addArticleToCart(data);
            setCurrentCart(newCart);
        } catch (ex) {
            logError(ex);
            handleError(ex, translations.cart.failedToAdd);
        } finally {
            isUpdating.value = false;
        }
    }

    async function removeAllCartDiscounts() {
        try {
            const updatedCart = await customerDao.removeAllCartDiscounts();
            setCurrentCart(updatedCart);
        } catch (ex) {
            logError(ex);
            handleError(ex, translations.cart.failedToRemove);
        } finally {
            isUpdating.value = false;
        }
    }

    async function removeItemFormCartAsync(item: ICartItem) {
        // remove item from cart, then run API call
        currentCart.value?.items.splice(currentCart.value?.items.indexOf(item), 1);

        isUpdating.value = true;

        try {
            const newCart = await customerDao.removeItemFromCart(item.id, {
                orderServiceId: orderServiceId.value!,
            });

            setCurrentCart(newCart);
        } catch (ex) {
            logError(ex);
            handleError(ex, translations.cart.failedToRemove);
        } finally {
            isUpdating.value = false;
        }
    }

    async function changeCartItemQuantityAsync(item: ICartItem, qty: number) {
        // update first, then run API call
        item.quantity = qty;

        isUpdating.value = true;

        try {
            const newCart = await customerDao.setItemQuantityInCart(item.id, {
                quantity: qty,
                orderServiceId: orderServiceId.value!,
            });

            setCurrentCart(newCart);
        } catch (ex) {
            logError(ex);
            handleError(ex, translations.cart.failedToChange);
        } finally {
            isUpdating.value = false;
        }
    }

    async function decreaseCartItemQuantity(item: ICartItem) {
        const newQuantity = item.quantity - 1;
        if (hasDiscountsOrVouchers.value) {
            await showConfirmDiscountsRemovalPopup(() => changeCartItemQuantityAsync(item, newQuantity));
        } else {
            await changeCartItemQuantityAsync(item, newQuantity);
        }
    }

    async function increaseCartItemQuantity(item: ICartItem) {
        const newQuantity = item.quantity + 1;
        if (hasDiscountsOrVouchers.value) {
            await showConfirmDiscountsRemovalPopup(() => changeCartItemQuantityAsync(item, newQuantity));
        } else {
            await changeCartItemQuantityAsync(item, newQuantity);
        }
    }

    async function removeCartItem(item: ICartItem) {
        if (hasDiscountsOrVouchers.value) {
            await showConfirmDiscountsRemovalPopup(() => removeItemFormCartAsync(item));
        } else {
            await removeItemFormCartAsync(item);
        }
    }

    async function setCartItemComment(item: ICartItem, comment: string) {
        // update first, then run API call
        item.comment = comment;

        isUpdating.value = true;

        try {
            const newCart = await customerDao.setCartItemComment(item.id, {
                comment: comment,
                orderServiceId: orderServiceId.value!,
            });

            setCurrentCart(newCart);
        } catch (ex) {
            logError(ex);
            handleError(ex, translations.cart.failedToChange);
        } finally {
            isUpdating.value = false;
        }
    }

    async function removeCartItemComment(item: ICartItem) {
        // update first, then run API call
        item.comment = undefined;

        isUpdating.value = true;

        try {
            const newCart = await customerDao.removeCartItemComment(item.id, {
                orderServiceId: orderServiceId.value!,
            });

            setCurrentCart(newCart);
        } catch (ex) {
            logError(ex);
            handleError(ex, translations.cart.failedToChange);
        } finally {
            isUpdating.value = false;
        }
    }

    function getCartItemQuantity(articleId: string) {
        const cartItems = getCartItems(articleId);

        let totalQuantity = 0;
        for (const cartItem of cartItems) {
            totalQuantity += cartItem.quantity;
        }
        return totalQuantity;
    }

    function getCartItems(articleId: string) {
        const foundCartItems: ICartItem[] = [];
        for (const item of items.value) {
            if (item.articleId == articleId) {
                foundCartItems.push(item);
            }
        }

        return foundCartItems;
    }

    async function loadCurrentCart() {
        try {
            isUpdating.value = true;
            const newCart = await customerDao.getCart();
            currentCart.value = newCart;
        } finally {
            isUpdating.value = false;
        }
    }

    function setCurrentCart(cart: ICustomerCart | null) {
        currentCart.value = cart;
    }

    function resetCart() {
        setCurrentCart(null);
    }

    async function deleteCartsFromLocation() {
        isUpdating.value = true;
        try {
            await customerDao.removeCartsFromLocation();
            currentCart.value = null;
        } finally {
            isUpdating.value = false;
        }
    }

    async function selectFulfillmentSlot(orderServiceId: string, selectedDate: Date, selectedTime: string) {
        isUpdating.value = true;
        try {
            await customerDao.selectCartFulfillmentSlot({
                orderServiceId: orderServiceId,
                selectedDate: selectedDate,
                selectedTime: selectedTime,
                socketConnectionId: undefined,
            });

            currentCart.value = null;
        } finally {
            isUpdating.value = false;
        }
    }

    function handleError(error: any, defaultMsg: string) {
        if (error instanceof DaoError) {
            switch (error.type) {
                case exceptionTypes.paymentInProgressException:
                    Popup.show({
                        message: translations.cart.paymentBlockingCart,
                    });
                    break;
                case exceptionTypes.openPriceIsMissingForArticleToCartException:
                case exceptionTypes.articleCannotBeAddedToCartException:
                    Popup.show({
                        message: translations.cart.failedToAdd,
                    });
                    break;

                default:
                    if (isDiscountsAppliedToCartException(error)) {
                        showConfirmDiscountsRemovalPopup();
                    }
                    break;
            }
        } else {
            Popup.show({ message: defaultMsg });
        }
    }

    function isDiscountsAppliedToCartException(error: any) {
        return error instanceof DaoError && error.type === exceptionTypes.discountsAppliedToCartException;
    }

    function showConfirmDiscountsRemovalPopup(onRemoveAllCartDiscountsConfirmed?: () => Promise<void>) {
        return new Promise<void>((resolve) => {
            const isShown = Popup.show({
                message: translations.cart.changingTotalWillRemoveAppliedDiscountsMessage,
                confirmHandler: async () => {
                    try {
                        const newCart = await customerDao.removeAllCartDiscounts();
                        setCurrentCart(newCart);

                        if (onRemoveAllCartDiscountsConfirmed) {
                            await onRemoveAllCartDiscountsConfirmed();
                        }
                        resolve();
                    } catch (error) {
                        logError(error);
                        handleError(error, translations.general.operationFailed);
                        resolve();
                    }
                },
                cancelHandler: () => {
                    loadCurrentCart();
                    resolve();
                },
                confirmButton: translations.cart.removeDiscounts,
                cancelButton: translations.general.cancel,
                highlightConfirm: false,
                highlightCancel: true,
                confirmBtnOnTheLeft: false,
                uniqueId: "changingTotalWillRemoveAppliedDiscountsPopup",
            });

            if (!isShown) {
                resolve();
            }
        });
    }

    function hasDiscountWithSingleItem(item: ICartItem) {
        return currentCart.value?.singleItemLevelDiscountItems.includes(item.id);
    }

    function showConfirmItemDiscountsRemovalPopup(item: ICartItem) {
        const message = hasDiscountWithSingleItem(item)
            ? translations.discounts.removeSingleItemDiscountPrompt
            : translations.discounts.removeAllItemDiscountsPrompt;

        Popup.show({
            message: message,
            confirmHandler: async () => {
                try {
                    const newCart = await customerDao.removeCartItemDiscounts(item.id, {
                        orderServiceId: orderServiceId.value!,
                    });
                    setCurrentCart(newCart);
                } catch (error) {
                    logError(error);
                    handleError(error, translations.general.operationFailed);
                }
            },
            confirmButton: translations.general.remove,
            cancelButton: translations.general.cancel,
            highlightConfirm: false,
            highlightCancel: true,
            confirmBtnOnTheLeft: false,
            uniqueId: "confirmItemDiscountsRemovalPopup",
        });
    }

    return {
        // state
        currentCart,
        isUpdating,

        // getters
        totalAmount,
        totalAfterDiscountsAndVouchers,
        totalCartItems,
        items,
        hasItems,
        hasDiscountsOrVouchers,
        orderService,
        areItemCommentsAllowed,

        // actions
        decreaseCartItemQuantity,
        increaseCartItemQuantity,
        removeCartItem,
        getCartItemQuantity,
        addArticleToCart,
        loadCurrentCart,
        setCurrentCart,
        resetCart, // todo: in pay context
        deleteCartsFromLocation,
        selectFulfillmentSlot,
        removeAllCartDiscounts,
        setCartItemComment,
        removeCartItemComment,
        showConfirmItemDiscountsRemovalPopup,
    };
});

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useCartStore, import.meta.hot));
}
